import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { postCall, deleteCall, getById, getCall, BASE_URL } from "../../utils/api";
import axios from "axios";
import toast from "react-hot-toast";

const initialState = {
  data: [],
  offerDetail: null,
  error: "",
  status: "idle",
  isUpdateStatus: "idle",
  onboardProcess: null,
  onboardProcessStatus: "idle",
};

export const getOffers = createAsyncThunk(
  "offers/get",
  async ({ payload, page = 1, per_page = 50 }) => {
    try {
      const response = await postCall(
        `/admin/v1/all_offer?page=${page}&per_page=${per_page}`,
        payload,
        false
      );
      return response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getOfferById = createAsyncThunk("offers/getById", async (id) => {
  try {
    const response = await getById("/admin/v1/show_offer", id, false);
    return await response;
  } catch (error) {
    return Promise?.reject(error);
  }
});

export const addOffer = createAsyncThunk("offers/add", async (offer) => {
  try {
    const response = await postCall("/admin/v1/add_offer", offer);
    return response;
  } catch (error) {
    return Promise?.reject(error);
  }
});

export const updateOffer = createAsyncThunk(
  "offers/update",
  async ({ id, formData }, { rejectWithValue }) => {
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/v1/update_offer/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.message || "Offer updated successfully!");
      return response.data;
    } catch (error) {
      if (error.response) {
        const message = error.response.data?.message || "Unable to update data. Please try again later.";
        toast.error(message);
        return rejectWithValue(error.response.data);
      } else {
        toast.error("Network error. Please try again.");
        return rejectWithValue("Network error");
      }
    }
  }
);

export const multipleDeleteOffer = createAsyncThunk("offers/delete", async (id) => {
  try {
    await deleteCall(`/admin/v1/delete_offer?offer_id=${id}`);
    return id;
  } catch (error) {
    return Promise?.reject(error);
  }
});

export const removeOffersByCard = createAsyncThunk(
    "offers/removeByCard",
    async ({ from_source, checked = 0 }) => {
      try {
        return await deleteCall(`/admin/v1/offer/remove-from-card`, {
          from_source,
          checked
        }, false);
      } catch (error) {
        return Promise?.reject(error);
      }
    }
);

export const onboardOffers = createAsyncThunk(
    "offers/onboardFromCrawlOffers",
    async ({ from_source }) => {
        try {
            return await postCall(`/admin/v1/transfer_offer_ref`, {
              from_source
            });
        } catch (error) {
            return Promise?.reject(error);
        }
    }
);

export const rollbackOffersByCard = createAsyncThunk(
    "offers/rollbackByCard",
    async ({ from_source, checked = 0 }) => {
      try {
        return await postCall(`/admin/v1/rollback_offer_ref`, {
          from_source,
          checked
        }, false);
      } catch (error) {
        return Promise?.reject(error);
      }
    }
);

export const initiateAsyncOnboard = createAsyncThunk(
    "offers/initiateAsyncOnboard",
    async ({ from_source }) => {
      try {
        return await postCall(`/admin/v1/transfer_offer_ref/async`, {
          from_source
        }, false);
      } catch (error) {
        return Promise?.reject(error);
      }
    }
);

export const getOnboardProgress = createAsyncThunk(
    "offers/getOnboardProgress",
    async ({ processId }) => {
      try {
        return await getCall(`/admin/v1/transfer_offer_ref/progress/${processId}`, false);
      } catch (error) {
        return Promise?.reject(error);
      }
    }
);

export const cancelOnboard = createAsyncThunk(
    "offers/cancelOnboard",
    async ({ processId }) => {
      try {
        return await deleteCall(`/admin/v1/transfer_offer_ref/cancel/${processId}`, {}, false);
      } catch (error) {
        return Promise?.reject(error);
      }
    }
);

export const getLatestOnboardProcess = createAsyncThunk(
    "offers/getLatestOnboardProcess",
    async ({ from_source }) => {
      try {
        return await postCall(`/admin/v1/transfer_offer_ref/latest`, { from_source }, false);
      } catch (error) {
        return Promise?.reject(error);
      }
    }
);

const offerSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    clearOnboardProcess: (state) => {
      state.onboardProcess = null;
      state.onboardProcessStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOffers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getOffers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(getOffers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(getOfferById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getOfferById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.offerDetail = action.payload;
      })
      .addCase(getOfferById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(addOffer.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addOffer.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(addOffer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(updateOffer.pending, (state) => {
        state.isUpdateStatus = "loading";
        state.error = null;
      })
      .addCase(updateOffer.fulfilled, (state, action) => {
        state.isUpdateStatus = "succeeded";
        // const index = state?.data?.data?.findIndex(
        //   (offer) => offer?.id === action?.payload?.data?.id
        // );
        // if (index >= 0) {
        //   state.data.data[index] = action.payload.data;
        // }
      })
      
      .addCase(updateOffer.rejected, (state, action) => {
        state.isUpdateStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(multipleDeleteOffer.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(multipleDeleteOffer.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = state.data.data.filter(
          (offer) => offer.id !== action.payload
        );
      })
      .addCase(multipleDeleteOffer.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(removeOffersByCard.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(removeOffersByCard.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(removeOffersByCard.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(onboardOffers.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(onboardOffers.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(onboardOffers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(rollbackOffersByCard.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(rollbackOffersByCard.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(rollbackOffersByCard.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(initiateAsyncOnboard.pending, (state) => {
        state.onboardProcessStatus = "loading";
        state.error = null;
      })
      .addCase(initiateAsyncOnboard.fulfilled, (state, action) => {
        state.onboardProcessStatus = "succeeded";
        state.onboardProcess = action.payload?.data;
      })
      .addCase(initiateAsyncOnboard.rejected, (state, action) => {
        state.onboardProcessStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(getOnboardProgress.pending, (state) => {
        state.onboardProcessStatus = "loading";
      })
      .addCase(getOnboardProgress.fulfilled, (state, action) => {
        state.onboardProcessStatus = "succeeded";
        state.onboardProcess = action.payload?.data;
      })
      .addCase(getOnboardProgress.rejected, (state, action) => {
        state.onboardProcessStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(cancelOnboard.pending, (state) => {
        state.onboardProcessStatus = "loading";
      })
      .addCase(cancelOnboard.fulfilled, (state, action) => {
        state.onboardProcessStatus = "succeeded";
        if (state.onboardProcess) {
          state.onboardProcess.status = action.payload?.data?.status || "cancelled";
        }
      })
      .addCase(cancelOnboard.rejected, (state, action) => {
        state.onboardProcessStatus = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
  },
});

export const { clearOnboardProcess } = offerSlice.actions;

export default offerSlice.reducer;
